import styled from "@emotion/styled/macro";
import {
  MOBILE_BREAKPOINT,
  SvgIcon,
  SvgNames,
  tokens,
  Typography,
} from "@sunrun/experience-ui-components";
import { ReviewOffer } from "../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import { OfferReviewComparisonGauge } from "components/molecules/offerReviewComparison/OfferReviewComparisonGauge";
import { OfferReviewComparisonHouseIllustration } from "components/molecules/offerReviewComparison/OfferReviewComparisonHouseIllustration";
import { getOfferReviewComparisonSelfSupplyPercent } from "utils/selfSupplyChartData";

const COMPARISON_FEATURE_LIST = [
  "Locked in predictable payment",
  "Avoid expensive peak-hour utility pricing",
  "Grid outage protection",
  "Distributed power plant program (if avail)",
  "Guaranteed energy access",
];

const PLACEHOLDER = {
  utility: {
    recapBadge: "Problem Recap",
    headline: "You're 100% dependent on the utility",
    bodyLines: [
      "Your prices are increasing and access to power is not guaranteed.",
    ],
    gaugePercent: 0,
    gaugeLabel: "No solar offset",
  },
  solar: {
    recapBadge: "Solution Recap",
    headline: "You're in control of your energy",
    bodyLines: [
      "Lock in predictable payments and guarantee your access to energy",
    ],
  },
};

type OfferReviewSystemComparisonSectionProps = {
  offer: ReviewOffer;
  utilityCompany?: string;
};

const OfferReviewSystemComparisonSection = ({
  offer,
  utilityCompany = "",
}: OfferReviewSystemComparisonSectionProps) => {
  const left = PLACEHOLDER.utility;
  const right = PLACEHOLDER.solar;
  const selfSupplyPercent = getOfferReviewComparisonSelfSupplyPercent(offer);

  return (
    <Section data-testid="offer-review-system-comparison">
      <Grid>
        <Card>
          <CardHeader>
            <RecapBadge variant="outline">{left.recapBadge}</RecapBadge>
            <RecapHeadline
              size={24}
              fontWeight="bold"
              color={tokens.BLACK}
              align="center"
            >
              {left.headline}
            </RecapHeadline>
            <RecapBody>
              {left.bodyLines.map((line) => (
                <RecapBodyLine
                  key={line}
                  size={14}
                  fontWeight="regular"
                  color={tokens.BLACK}
                  align="center"
                >
                  {line}
                </RecapBodyLine>
              ))}
            </RecapBody>
          </CardHeader>
          <OfferReviewComparisonHouseIllustration
            variant="utility"
            sunrunPercent={0}
          />
          <ListSection>
            <GaugeSlot>
              <OfferReviewComparisonGauge
                percent={left.gaugePercent}
                variant="negative"
                label={left.gaugeLabel}
                providerName={utilityCompany}
              />
            </GaugeSlot>
            <List>
              {COMPARISON_FEATURE_LIST.map((item) => (
                <ListRow key={item}>
                  <ListRowIcon aria-hidden>
                    <SvgIcon
                      name={SvgNames.CloseFilled}
                      width={20}
                      height={20}
                      color={tokens.TINTS_MEDIUM_BLUE_60}
                    />
                  </ListRowIcon>
                  <Typography size={12} color={tokens.BLACK}>
                    {item}
                  </Typography>
                </ListRow>
              ))}
            </List>
          </ListSection>
        </Card>

        <Card>
          <CardHeader>
            <RecapBadge variant="filled">{right.recapBadge}</RecapBadge>
            <RecapHeadline
              size={24}
              fontWeight="bold"
              color={tokens.BLACK}
              align="center"
            >
              {right.headline}
            </RecapHeadline>
            <RecapBody>
              {right.bodyLines.map((line) => (
                <RecapBodyLine
                  key={line}
                  size={14}
                  fontWeight="regular"
                  color={tokens.BLACK}
                  align="center"
                >
                  {line}
                </RecapBodyLine>
              ))}
            </RecapBody>
          </CardHeader>
          <OfferReviewComparisonHouseIllustration
            variant="solar"
            sunrunPercent={selfSupplyPercent}
          />
          <ListSection>
            <GaugeSlot>
              <OfferReviewComparisonGauge
                percent={selfSupplyPercent}
                variant="positive"
                label=""
              />
            </GaugeSlot>
            <List>
              {COMPARISON_FEATURE_LIST.map((item) => (
                <ListRow key={item}>
                  <ListRowIcon aria-hidden>
                    <SvgIcon
                      name={SvgNames.CheckFilled}
                      width={20}
                      height={20}
                      color={tokens.SUCCESS_60}
                    />
                  </ListRowIcon>
                  <Typography size={12} color={tokens.BLACK}>
                    {item}
                  </Typography>
                </ListRow>
              ))}
            </List>
          </ListSection>
        </Card>
      </Grid>
    </Section>
  );
};

const Section = styled.section`
  box-sizing: border-box;
  max-width: 1200px;
  width: calc(100% - 128px);
  margin: 0 auto 48px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: calc(100% - 32px);
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const Card = styled.article`
  background: color-mix(in srgb, ${tokens.WHITE} 30%, transparent);
  border-radius: 10px;
  padding: 24px 20px 28px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-height: 520px;
`;

const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
`;

const RecapBadge = styled.span<{ variant: "outline" | "filled" }>`
  padding: 4px 14px;
  border-radius: 11px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  ${({ variant }) =>
    variant === "filled"
      ? `
    background: ${tokens.BLACK};
    color: ${tokens.WHITE};
  `
      : `
    background: transparent;
    color: ${tokens.BLACK};
    border: 1px solid ${tokens.BLACK};
  `}
`;

const RecapHeadline = styled(Typography)`
  width: 100%;
  line-height: 1.25;
  text-align: center;
`;

const RecapBody = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const RecapBodyLine = styled(Typography)`
  display: block;
  line-height: 1.4;
  text-align: center;
`;

const ListSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: auto;
  width: 100%;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
  }
`;

const GaugeSlot = styled.div`
  flex-shrink: 0;
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  margin-top: auto;
  padding: 0;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 8px;
`;

const ListRow = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  text-align: left;
`;

const ListRowIcon = styled.span`
  display: flex;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;

  svg {
    display: block;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`;

export { OfferReviewSystemComparisonSection };
